import QueryDropdown, { QueryDropdownMultiProps, QueryDropdownProps } from '@/components/QueryDropdown';
import { ReactNode, useCallback } from 'react';
import { getAirportGroupPermissions, getAirportGroups } from '@/api/services/filters/airportGroups';

import { DropdownItem } from '@/components/Dropdown';
import { FilterOption } from '@/models/gen/graphql';
import { fieldWrapper } from '@/components/Field';
import { getAirports } from '@/api/services/filters/airports';

export type AirportDropdownProps = Omit<QueryDropdownProps, 'query'>;
export type AirportGroupDropdownProps = Omit<QueryDropdownMultiProps, 'query'>;

const AirportDropdown = ({ name = 'AirportDropdown', ...props }: AirportDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const res = await getAirports();
    return res.map?.(
      (option: FilterOption): DropdownItem => ({ label: option.displayName, value: option.id, keywords: option.displayName.split(' ') })
    );
  }, []);

  return (
    <QueryDropdown
      {...props}
      name={name}
      query={getItems}
      options={{ ...props?.options, locale: props?.options?.locale || { 'Select...': 'City' } }}
    />
  );
};

export const AirportDropdownMulti = ({ name = 'AirportDropdown', ...props }: AirportGroupDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const res = await getAirports();
    return res.map?.(
      (option: FilterOption): DropdownItem => ({ label: option.displayName, value: option.id, keywords: option.displayName.split(' ') })
    );
  }, []);

  return (
    <QueryDropdown.Multi
      {...props}
      name={name}
      query={getItems}
      options={{ ...props?.options, locale: props?.options?.locale || { 'Select...': 'City' } }}
    />
  );
};
AirportDropdown.Multi = AirportDropdownMulti;

export const AirportGroupDropdown = ({ name = 'AirportGroupDropdown', ...props }: AirportGroupDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const res = await getAirportGroups();
    return (
      res?.map?.(
        (option: FilterOption): DropdownItem => ({
          label: option?.displayName,
          value: option?.values?.length === 1 && option?.values?.[0] === option?.id ? option?.id : undefined,
          group: option?.values?.length > 1 ? option?.values : undefined,
          keywords: option?.displayName?.split?.(' ') || [],
        })
      ) || []
    );
  }, []);

  return (
    <QueryDropdown.Sticky
      {...props}
      name={name}
      query={getItems}
      onChange={(value, item) => {
        props?.onChange?.(value, item);
        return false;
      }}
      options={{ ...props?.options, locale: props?.options?.locale || { 'Select...': 'City' }, showSelectAll: false }}
    />
  );
};

export const AirportGroupDropdownMulti = ({ name = 'AirportGroupDropdown', ...props }: AirportGroupDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const res = await getAirportGroups();
    return [
      {
        label: 'All Airports',
        value: undefined,
        group:
          res
            ?.filter?.((option: FilterOption) => option?.values?.includes?.(option?.id))
            ?.map?.((option: FilterOption) => option.id)
            .flat() || [],
        keywords: ['All'],
      },
      ...(res?.map?.(
        (option: FilterOption): DropdownItem => ({
          label: option?.displayName,
          value: option?.values?.length === 1 && option?.values?.[0] === option?.id ? option?.id : undefined,
          group: option?.values?.length > 1 ? option?.values : undefined,
          keywords: option?.displayName?.split?.(' ') || [],
        })
      ) || []),
    ];
  }, []);

  return (
    <QueryDropdown.Multi
      {...props}
      name={name}
      query={getItems}
      options={{ ...props?.options, locale: props?.options?.locale || { 'Select...': 'City' }, showSelectAll: false }}
    />
  );
};

AirportGroupDropdown.Multi = AirportGroupDropdownMulti;

export const AirportGroupPermissionsDropdown = ({
  name = 'AirportGroupPermissionsDropdown',
  ...props
}: AirportGroupDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const res = await getAirportGroupPermissions();
    return res.map?.(
      (option: FilterOption): DropdownItem => ({
        label: option?.displayName,
        value: option?.values?.length === 1 && option?.values?.[0] === option?.id ? option?.id : undefined,
        group: option?.values?.length > 1 ? option?.values : undefined,
        keywords: option?.displayName?.split?.(' ') || [],
      })
    );
  }, []);

  return (
    <QueryDropdown.Multi
      {...props}
      name={name}
      query={getItems}
      options={{ ...props?.options, locale: props?.options?.locale || { 'Select...': 'City' } }}
    />
  );
};

export const AirportDropdownField = fieldWrapper<AirportDropdownProps>(AirportDropdown);
export const AirportDropdownMultiField = fieldWrapper<AirportDropdownProps>(AirportDropdownMulti);
export const AirportGroupDropdownField = fieldWrapper<AirportGroupDropdownProps>(AirportGroupDropdown);

export default AirportDropdown;
